import { useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeftRight, Check } from "lucide-react";

const FIELDS = [["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["service_interested", "Service"], ["lead_source", "Source"],
  ["status", "Status"], ["assigned_staff", "Assigned to"], ["next_followup", "Next follow-up"], ["rating", "Rating"]];
const norm = (v) => (v ?? "").toString().trim();
const defaultChoices = (keep, other) => Object.fromEntries(FIELDS.map(([k]) => [k, !norm(keep[k]) && norm(other[k]) ? "other" : "keep"]));

const Cell = ({ value, picked, differ, onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`text-left px-3 py-2 rounded-lg border text-sm min-h-[38px] flex items-center justify-between gap-2 transition-colors ${picked ? "border-primary bg-primary/5 text-slate-900 font-semibold" : differ ? "border-amber-200 bg-amber-50 text-slate-500" : "border-slate-200 text-slate-400"}`}>
    <span className="truncate">{value || <span className="italic font-normal text-slate-300">blank</span>}</span>
    {picked && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
  </button>
);

export function LeadMergeDialog({ lead, other, staff, onClose, onMerged }) {
  const canSwap = !lead.converted_client_id && !other.converted_client_id;
  const [swapped, setSwapped] = useState(!!other.converted_client_id && !lead.converted_client_id);
  const [keep, dup] = swapped ? [other, lead] : [lead, other];
  const [choices, setChoices] = useState(() => defaultChoices(keep, dup));
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const staffName = useMemo(() => Object.fromEntries((staff || []).map((s) => [s.id, s.name])), [staff]);
  const show = (k, l) => (k === "assigned_staff" ? staffName[l[k]] || "" : k === "next_followup" ? norm(l[k]).slice(0, 10) : norm(l[k]));
  const swap = () => { const n = !swapped; setSwapped(n); const [k2, d2] = n ? [other, lead] : [lead, other]; setChoices(defaultChoices(k2, d2)); setConfirming(false); };
  const finalName = choices.name === "other" ? dup.name : keep.name;

  const merge = async () => {
    setBusy(true);
    try {
      await api.post(`/leads/${keep.id}/merge`, { other_id: dup.id, choices });
      toast.success("Leads merged"); onMerged();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="lead-merge-dialog">
        <DialogHeader><DialogTitle>Merge duplicate leads</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Pick the value to keep for each field. Fields that differ are highlighted. Notes from both leads are combined, and appointments and history move to the kept lead.</p>
        <div className="grid grid-cols-[110px_1fr_1fr] gap-2 items-center text-xs">
          <span />
          <span className="font-bold text-emerald-700 uppercase tracking-wide" data-testid="merge-keep-label">Kept · {keep.name}</span>
          <span className="font-bold text-rose-600 uppercase tracking-wide" data-testid="merge-dup-label">Removed · {dup.name}</span>
          {FIELDS.map(([k, label]) => {
            const differ = show(k, keep) !== show(k, dup);
            return [
              <span key={`${k}-l`} className="text-slate-500 font-medium">{label}</span>,
              <Cell key={`${k}-a`} testid={`merge-${k}-keep`} value={show(k, keep)} picked={choices[k] === "keep"} differ={differ} onClick={() => setChoices({ ...choices, [k]: "keep" })} />,
              <Cell key={`${k}-b`} testid={`merge-${k}-other`} value={show(k, dup)} picked={choices[k] === "other"} differ={differ} onClick={() => setChoices({ ...choices, [k]: "other" })} />,
            ];
          })}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          <button type="button" data-testid="merge-swap" onClick={swap} disabled={!canSwap}
            title={canSwap ? "" : "The lead already converted to a client is always kept"}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 disabled:opacity-40">
            <ArrowLeftRight className="h-4 w-4" /> Swap which lead stays
          </button>
          {!confirming && <button type="button" data-testid="merge-continue" onClick={() => setConfirming(true)} className="bg-primary text-white px-5 py-2.5 rounded-xl font-semibold text-sm">Merge leads</button>}
        </div>
        {confirming && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 space-y-2" data-testid="merge-confirm-box">
            <div>Merge <b>{dup.name}</b> into <b>{finalName}</b>? The duplicate will be deleted. This can't be undone.</div>
            <div className="flex gap-2">
              <button type="button" data-testid="merge-confirm" onClick={merge} disabled={busy} className="bg-rose-600 text-white px-4 py-2 rounded-xl font-semibold text-sm disabled:opacity-60">{busy ? "Merging…" : "Yes, merge"}</button>
              <button type="button" data-testid="merge-cancel" onClick={() => setConfirming(false)} className="px-4 py-2 rounded-xl border border-rose-200 text-sm font-semibold">Cancel</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
